import React from "react";
import Drawer from "@mui/material/Drawer";
import OperationsPageHeader from "../home/top_bar_components/OperationsPageHeader";
import LoadSpinner from "../../../utilities/LoadSpinner";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerSmall from "../requests/shared/helpers/DrawerFooterContainerSmall";
import ButtonFilled from "../requests/shared/helpers/ButtonFilled";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_my_tips } from "../../../constants";
import { dollars } from "./TipsDisplay";

// Time & Payroll > Tips for employees: the signed-in person's tips at the
// selected location, one row per week a manager has confirmed, with the
// week's breakdown in a drawer. Last week shows as waiting until it is
// confirmed, with no amounts.

const parseIso = (text) => {
  const [y, m, d] = String(text).split("-").map(Number);
  return new Date(y, m - 1, d);
};

// "Oct 13–19", "Sep 29–Oct 5" across a month, or "Dec 29, 2025–Jan 4, 2026"
// across a year (a long pay period can run a year).
export const weekLabel = (start, end) => {
  const first = parseIso(start);
  const last = parseIso(end);
  const month = (date) => date.toLocaleDateString("en-US", { month: "short" });
  if (first.getFullYear() !== last.getFullYear()) {
    return `${month(first)} ${first.getDate()}, ${first.getFullYear()}–${month(last)} ${last.getDate()}, ${last.getFullYear()}`;
  }
  if (first.getMonth() === last.getMonth()) return `${month(first)} ${first.getDate()}–${last.getDate()}`;
  return `${month(first)} ${first.getDate()}–${month(last)} ${last.getDate()}`;
};

const hours = (value) => (Number(value) || 0).toFixed(2);

const adjusted = (week) => Boolean(week.adjustments?.service_charge_cents || week.adjustments?.tips_cents);

const confirmedOn = (timestamp) =>
  timestamp ? new Date(timestamp).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : null;

export default function TipsDisplayEmployee(props) {
  const [data, setData] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  const [openWeek, setOpenWeek] = React.useState(null);
  const ticket = React.useRef(0);

  React.useEffect(() => {
    const current = ++ticket.current;
    setData(null);
    setFailed(false);
    setOpenWeek(null);
    getRightworkAPI(API_get_my_tips, { company_id: props.homeCompany, store_id: props.homeStore })
      .then((res) => {
        if (current === ticket.current) setData(res.data?.redux?.payload || null);
      })
      .catch(() => {
        if (current === ticket.current) setFailed(true);
      });
  }, [props.homeCompany, props.homeStore]);

  const weeks = data?.weeks || [];
  const pending = data?.pending;

  const table = (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="px-4 py-3 font-semibold">Week</th>
            <th className="px-4 py-3 text-right font-semibold">Hours</th>
            <th className="px-4 py-3 text-right font-semibold">Service charge</th>
            <th className="px-4 py-3 text-right font-semibold">Tips</th>
            <th className="px-4 py-3 text-right font-semibold">Total</th>
          </tr>
        </thead>
        <tbody>
          {pending && (
            <tr className="border-b border-slate-100">
              <td className="whitespace-nowrap px-4 py-3">
                <div className="font-semibold text-slate-900">{weekLabel(pending.start_date, pending.end_date)}</div>
                <span className="mt-1.5 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Waiting on manager</span>
              </td>
              <td colSpan={4} className="px-4 py-3 text-right text-[13px] text-slate-500">
                Amounts show here once your manager confirms this week.
              </td>
            </tr>
          )}
          {weeks.map((week) => (
            <tr
              key={`${week.start_date}-${week.end_date}`}
              className="cursor-pointer border-b border-slate-100 last:border-b-0 hover:bg-violet-50"
              onClick={() => setOpenWeek(week)}
            >
              <td className="whitespace-nowrap px-4 py-3">
                <button type="button" className="font-semibold text-slate-900 hover:text-violet-700" onClick={(event) => { event.stopPropagation(); setOpenWeek(week); }}>
                  {weekLabel(week.start_date, week.end_date)}
                </button>
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-slate-600">{hours(week.hours)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-slate-900">{dollars(week.service_charge_cents)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-slate-900">{dollars(week.tips_cents)}</td>
              <td className="px-4 py-3 text-right font-bold tabular-nums text-slate-900">{dollars(week.total_cents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const body = () => {
    if (failed) return <p className="text-sm text-slate-500">Your tips could not be loaded. Try again in a moment.</p>;
    if (!data) return <div className="flex justify-center py-16"><LoadSpinner /></div>;
    if (!weeks.length && !pending) {
      return (
        <div className="rounded-xl border border-slate-200 bg-white px-6 py-12 text-center">
          <p className="text-sm font-semibold text-slate-900">No tips to show yet</p>
          <p className="mt-1 text-sm text-slate-500">Weeks show here after your manager confirms them.</p>
        </div>
      );
    }
    return table;
  };

  const drawer = (
    <Drawer open={Boolean(openWeek)} anchor="right" onClose={() => setOpenWeek(null)}>
      {openWeek && (
        <section aria-label={`${weekLabel(openWeek.start_date, openWeek.end_date)} tips`} className="relative h-full w-[480px] max-w-full">
          <div className="max-h-screen overflow-auto">
            <DrawerHeader title={weekLabel(openWeek.start_date, openWeek.end_date)} onClose={() => setOpenWeek(null)} />
            <div className="flex w-full flex-col gap-5 px-6">
              <p className="text-[13px] text-slate-500">
                {[data?.store_name, confirmedOn(openWeek.confirmed_at) && `Confirmed ${confirmedOn(openWeek.confirmed_at)}`].filter(Boolean).join(" · ")}
              </p>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total take home</div>
                <div className="text-3xl font-bold tabular-nums text-slate-900">{dollars(openWeek.total_cents)}</div>
                <div className="text-[13px] text-slate-500">{hours(openWeek.hours)} hrs</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-xs text-slate-600">Service charge</div>
                  <div className="text-base font-semibold tabular-nums text-slate-900">{dollars(openWeek.service_charge_cents)}</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-xs text-slate-600">Tips</div>
                  <div className="text-base font-semibold tabular-nums text-slate-900">{dollars(openWeek.tips_cents)}</div>
                </div>
              </div>
              {(openWeek.pools.length > 0 || adjusted(openWeek)) && (
                <div className="flex flex-col gap-2.5">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">By pool</div>
                  {openWeek.pools.map((pool, index) => (
                    <div key={`${pool.pool_name}-${pool.role_name}-${index}`} className="flex justify-between gap-3 rounded-lg border border-slate-200 px-3.5 py-3">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-semibold text-slate-900">{pool.pool_name}</span>
                        <span className="text-[13px] text-slate-500">{pool.role_name} · {hours(pool.hours)} hrs</span>
                        <span className="text-xs text-slate-500">Service charge {dollars(pool.service_charge_cents)} · Tips {dollars(pool.tips_cents)}</span>
                      </div>
                      <span className="text-[15px] font-bold tabular-nums text-slate-900">{dollars(pool.total_cents)}</span>
                    </div>
                  ))}
                  {adjusted(openWeek) && (
                    <div className="flex justify-between gap-3 rounded-lg border border-slate-200 px-3.5 py-3">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-semibold text-slate-900">Adjusted by your manager</span>
                        <span className="text-xs text-slate-500">
                          Service charge {dollars(openWeek.adjustments.service_charge_cents, { sign: true })} · Tips {dollars(openWeek.adjustments.tips_cents, { sign: true })}
                        </span>
                      </div>
                      <span className="text-[15px] font-bold tabular-nums text-slate-900">
                        {dollars(openWeek.adjustments.service_charge_cents + openWeek.adjustments.tips_cents, { sign: true })}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="h-[120px]" />
          </div>
          <DrawerFooterContainerSmall>
            <ButtonFilled text="Done" onClick={() => setOpenWeek(null)} />
          </DrawerFooterContainerSmall>
        </section>
      )}
    </Drawer>
  );

  return (
    <div className="min-h-screen bg-white">
      <div className="my-5 pb-16">
        <OperationsPageHeader
          title="Tips"
          eyebrow="Time & Payroll"
          scope="store"
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          change_home_store={props.change_home_store}
        >
          <div className="mt-6">{body()}</div>
        </OperationsPageHeader>
      </div>
      {drawer}
    </div>
  );
}
